export type Environment = 'prod' | 'next' | 'test' | 'dev' | `temp-${string}` | 'local'

const patterns = [
  /(https|http):\/\/((?<part1>(admin|www))\.|)((?<tempEnv>.*)\.|)experienceops(-(?<env>dev|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>(vehicles|waivers|ridersafety|outfitter|mpwr-commandcenter|mpwr-hq)(-prod)?)\.)((?<tempEnv>.*)\.|)poladv(-(?<env>dev|dev2|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>adventures)\.)((?<tempEnv>.*)\.|)polaris(-(?<env>dev|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>adventures-local)\.)((?<tempEnv>.*)\.|)polaris(-(?<env>dev|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>adventures-origin)\.)((?<tempEnv>.*)\.|)polaris(-(?<env>dev|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>adventures-preview)\.)((?<tempEnv>.*)\.|)polaris(-(?<env>dev|test|next)|)\.(?<zone>com|run)/,
  /(https|http):\/\/((?<part1>www)\.|)((?<tempEnv>.*)\.|)arrivalhq(-(?<env>dev|test|next)|)\.(?<zone>com|run)/
]

export function GetEnvironmentFromDomain(domain: string): Environment {
  // console.log(`GetEnvironmentFromDomain domain=${domain}`)
  if (
    domain.includes('localhost') ||
    domain.includes('http://127.0.0.1') ||
    domain.includes('polaris-outfitter-web.ngrok.io')
  ) {
    return 'local'
  } else {
    const pattern = patterns.find((pattern) => domain.match(pattern))
    if (pattern) {
      const groups = domain.match(pattern)?.groups || {}
      if (groups.env === 'dev2') {
        groups.env = 'dev'
      }
      if (groups.env === 'dev' && groups.tempEnv) {
        return `temp-${groups.tempEnv}`
      } else {
        if (groups.env) {
          if (['dev', 'test', 'next'].includes(groups.env)) {
            return groups.env as Environment
          } else {
            throw new Error(`Unrecognized environment ${groups.env} for domain ${domain}`)
          }
        } else {
          return 'prod'
        }
      }
    } else {
      throw new Error(`Unrecognized environment for domain "${domain}" - none pattern fits`)
    }
  }
}

export function GetExternalUrlsForSite(
  env: Environment,
  experienceOps: boolean = false
): {
  AdminWeb: string
  CustomerWeb: string
  CustomerWebPreview: string
  EhsWeb: string
  OutfitterWeb: string
  VlpWeb: string
  WaiverWeb: string
  BackendApi: string
  RocketWeb: string
} {
  console.log('[Capacitor] GetExternalUrlsForSite', env)
  switch (env) {
    case 'prod':
      return {
        AdminWeb: 'https://mpwr-commandcenter.poladv.com',
        CustomerWeb: 'https://adventures.polaris.com',
        CustomerWebPreview: 'https://adventures-preview.polaris.com',
        EhsWeb: 'https://ridersafety.poladv.com',
        OutfitterWeb: 'https://mpwr-hq.poladv.com',
        VlpWeb: 'https://vehicles.poladv.com',
        WaiverWeb: 'https://waivers.poladv.com',
        BackendApi: experienceOps ? 'https://api.experienceops.com' : 'https://poladv-api.polaris.com',
        RocketWeb: 'https://rocket.poladv.com'
      }
    case 'next':
      return {
        AdminWeb: 'https://admin.experienceops-next.com',
        CustomerWeb: 'https://adventures.polaris-next.com',
        CustomerWebPreview: 'https://adventures-preview.polaris-next.com',
        EhsWeb: 'https://ridersafety.poladv-next.com',
        OutfitterWeb: 'https://outfitter.poladv-next.com',
        VlpWeb: 'https://vehicles.poladv-next.com',
        WaiverWeb: 'https://waivers.poladv-next.com',
        BackendApi: experienceOps ? 'https://api.experienceops.com' : 'https://poladv-api.polaris.com',
        RocketWeb: 'https://rocket.poladv-next.com'
      }
    case 'test':
      return {
        AdminWeb: 'https://admin.experienceops-test.run',
        CustomerWeb: 'https://adventures.polaris-test.run',
        CustomerWebPreview: 'https://adventures-preview.polaris-test.run',
        EhsWeb: 'https://ridersafety.poladv-test.run',
        OutfitterWeb: 'https://mpwr-hq.poladv-test.run',
        VlpWeb: 'https://vehicles.poladv-test.run',
        WaiverWeb: 'https://waivers.poladv-test.run',
        BackendApi: 'https://poladv-api.polaris-test.run',
        RocketWeb: 'https://rocket.poladv-test.run'
      }
    case 'local':
    case 'dev':
      return {
        AdminWeb: 'https://admin.experienceops-dev.com',
        CustomerWeb: 'https://adventures.polaris-dev.com',
        CustomerWebPreview: 'https://adventures-preview.polaris-dev.com',
        EhsWeb: 'https://ridersafety.poladv-dev.com',
        OutfitterWeb: 'https://mpwr-hq.poladv-dev.com',
        VlpWeb: 'https://vehicles.poladv-dev.com',
        WaiverWeb: 'https://waivers.poladv-dev.com',
        BackendApi: experienceOps ? 'https://api.experienceops-dev.com' : 'https://poladv-api.polaris-dev.com',
        RocketWeb: 'https://rocket.poladv-dev.com'
      }
    default: {
      const regex = env.match(/temp-(?<env>.*)/)
      if (regex && regex.groups?.env) {
        return {
          AdminWeb: `https://admin.${regex.groups.env}.experienceops-dev.com`,
          CustomerWeb: `https://adventures.${regex.groups.env}.polaris-dev.com`,
          CustomerWebPreview: `https://adventures-preview.${regex.groups.env}.polaris-dev.com`,
          EhsWeb: `https://ridersafety.${regex.groups.env}.poladv-dev.com`,
          OutfitterWeb: `https://outfitter.${regex.groups.env}.poladv-dev.com`,
          VlpWeb: `https://vehicles.${regex.groups.env}.poladv-dev.com`,
          WaiverWeb: `https://waivers.${regex.groups.env}.poladv-dev.com`,
          BackendApi: experienceOps ? 'https://api.experienceops-dev.com' : 'https://poladv-api.polaris-dev.com',
          RocketWeb: 'https://rocket.poladv-dev.com'
        }
      } else {
        throw new Error(`Unrecognized Env: ${env}`)
      }
    }
  }
}

export function GetExternalUrlsFromDomain(
  app: 'AdminWeb' | 'CustomerWeb' | 'OutfitterWeb' | 'EhsWeb' | 'WaiverWeb',
  domain: string,
  useDevBackendLocally?: string
) {
  const environment = GetEnvironmentFromDomain(domain)
  const siteUrls = GetExternalUrlsForSite(environment)
  const BackendApi = (() => {
    if (environment === 'local') {
      if (app === 'CustomerWeb') {
        return `${domain}/api`
      } else if (useDevBackendLocally === 'true') {
        return 'https://admin.experienceops-dev.com/api'
      } else {
        return 'http://localhost:8443/api'
      }
    } else {
      return `${domain}/api`
    }
  })()
  return { ...siteUrls, BackendApi }
}
